/**
 * Search Accordion
 * ================
 *
 * Desktop search accordion functionality that integrates with the mega menu system.
 * - Opens/closes search panel with smooth animation
 * - Closes mega menus when search opens
 * - Closes search when mega menu opens
 * - Desktop only (>= 1280px)
 *
 * @since 2.0.0
 */

import { TIMING } from './menu-utils.js'
import { NAV_DESKTOP_MQ } from './breakpoints.js'

class SearchAccordion {
  constructor () {
    // Store media query matcher
    this.mediaQuery = NAV_DESKTOP_MQ

    // Cache DOM elements
    this.searchToggle = null
    this.searchPanel = null
    this.navigation = null
    this.megaMenuItems = []

    // Bind methods
    this.handleToggleClick = this.handleToggleClick.bind(this)
    this.handleMegaMenuOpen = this.handleMegaMenuOpen.bind(this)
    this.handleMediaChange = this.handleMediaChange.bind(this)

    // Initialize
    this.init()
  }

  /**
   * Initialize search accordion
   */
  init () {
    // Cache DOM elements
    this.searchToggle = document.querySelector('[data-search-toggle]')
    this.searchPanel = document.querySelector('#site-header-search')
    this.navigation = document.querySelector('.site-header__navigation')

    if (!this.searchToggle || !this.searchPanel || !this.navigation) {
      return
    }

    // Get all top-level menu items
    this.megaMenuItems = Array.from(
      this.navigation.querySelectorAll('.menu-level-0')
    )

    // Check initial viewport state
    this.handleMediaChange(this.mediaQuery)

    // Listen for viewport changes
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleMediaChange)
    } else {
      this.mediaQuery.addListener(this.handleMediaChange)
    }
  }

  /**
   * Handle viewport changes
   */
  handleMediaChange (mq) {
    if (mq.matches) {
      // Desktop: Enable search accordion
      this.enable()
    } else {
      // Mobile: Disable and close
      this.disable()
    }
  }

  /**
   * Enable search accordion
   */
  enable () {
    // Add click handler for search toggle
    this.searchToggle.addEventListener('click', this.handleToggleClick)

    // Listen for mega menu openings
    this.megaMenuItems.forEach(item => {
      const trigger = item.querySelector(':scope > a[data-accordion-trigger]')
      if (trigger) {
        trigger.addEventListener('click', this.handleMegaMenuOpen)
      }
    })
  }

  /**
   * Disable search accordion
   */
  disable () {
    // Remove click handler
    this.searchToggle.removeEventListener('click', this.handleToggleClick)

    // Remove mega menu listeners
    this.megaMenuItems.forEach(item => {
      const trigger = item.querySelector(':scope > a[data-accordion-trigger]')
      if (trigger) {
        trigger.removeEventListener('click', this.handleMegaMenuOpen)
      }
    })

    // Remove media query listener
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange)
    } else {
      this.mediaQuery.removeListener(this.handleMediaChange)
    }

    // Close search if open
    if (this.searchPanel.getAttribute('aria-hidden') === 'false') {
      this.closeSearch()
    }
  }

  /**
   * Handle search toggle click
   */
  handleToggleClick (event) {
    event.preventDefault()

    const isExpanded = this.searchToggle.getAttribute('aria-expanded') === 'true'

    if (isExpanded) {
      this.closeSearch()
    } else {
      this.openSearch()
    }
  }

  /**
   * Handle mega menu opening - close search
   */
  handleMegaMenuOpen () {
    // Close search when any mega menu item is clicked
    if (this.searchPanel.getAttribute('aria-hidden') === 'false') {
      this.closeSearch()
    }
  }

  /**
   * Open search panel
   */
  openSearch () {
    // Close all mega menus first
    this.closeMegaMenus()

    // Update toggle button state
    this.searchToggle.setAttribute('aria-expanded', 'true')
    this.searchToggle.classList.add('is-active')

    // Show panel
    this.searchPanel.setAttribute('aria-hidden', 'false')
    this.searchPanel.classList.add('is-expanded')

    // Calculate and set max-height for smooth animation
    // Use scrollHeight of the panel itself to get full content height
    this.searchPanel.style.maxHeight = this.searchPanel.scrollHeight + 'px'

    // Focus search input after animation
    setTimeout(() => {
      const input = this.searchPanel.querySelector('input[type="search"]')
      if (input) {
        input.focus()
      }
    }, TIMING.ACCORDION_OPEN)
  }

  /**
   * Close search panel
   */
  closeSearch () {
    // Update toggle button state
    this.searchToggle.setAttribute('aria-expanded', 'false')
    this.searchToggle.classList.remove('is-active')

    // Hide panel
    this.searchPanel.style.maxHeight = '0'

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.searchPanel.setAttribute('aria-hidden', 'true')
      this.searchPanel.classList.remove('is-expanded')
    }, TIMING.ACCORDION_OPEN)
  }

  /**
   * Close all mega menus
   */
  closeMegaMenus () {
    this.megaMenuItems.forEach(item => {
      const trigger = item.querySelector(':scope > a[data-accordion-trigger]')
      if (trigger && item.classList.contains('is-expanded')) {
        trigger.click()
      }
    })
  }
}

/**
 * Initialize search accordion
 */
export function initSearchAccordion () {
  const searchAccordion = new SearchAccordion()
  return searchAccordion
}
